import type { FlightMoneySource } from '../results/flight_offer_source.js';

/*
 * As tarifas como as telas de voo do kit as LEEM, declaradas pela forma (#4997):
 * o kit não importa o domain. Os vocabulários são as MESMAS listas do domain
 * (`FARE_DIMENSIONS`, `FarePolarity`, o estado do atributo), e o genui-kit prova
 * no typecheck que os tipos do fio cabem aqui.
 */

/** As dimensões dos cards de família, na ordem do agrupamento visual: bagagem
 *  (mão → despacho) → assento → flexibilidade (remarcação → reembolso). */
export const FARE_DIMENSIONS = ['carry', 'checked', 'seat', 'change', 'refund'] as const;
export type FareDimension = (typeof FARE_DIMENSIONS)[number];

/** O que uma frase do fornecedor diz de uma dimensão. */
export type FarePolarity = 'affirmed' | 'negated' | 'conflict';

/** O classificador do texto livre do fornecedor: as dimensões que a frase toca,
 *  cada uma com a sua polaridade. É regra do domain, compartilhada com o
 *  modal-session — o host passa o `polaritiesOf` dele. */
export type FareTextClassifier = (
  text: string,
) => readonly { dimension: FareDimension; polarity: FarePolarity }[];

/** O que o fornecedor (ou o mapa curado da VOLL) declarou de uma dimensão. */
export type FareAttributeState = 'included' | 'fee' | 'not_offered' | 'unknown';

/** Uma regra que reprovou a tarifa — a mesma forma do motivo da justificativa do
 *  checkout; `params` só nas de antecedência. */
export interface FarePolicyViolationSource {
  readonly key: string;
  readonly params?: { readonly days: number; readonly scope: 'domestic' | 'international' };
}

/** A franquia de um volume que a tarifa declara (#6542): `null` é "não disse". */
interface FareAllowanceLineSource {
  readonly pieces: number | null;
  readonly weightKg: number | null;
}

export interface FlightFareSource {
  fareId: string;
  name: string;
  price: FlightMoneySource;
  withinPolicy?: boolean | null;
  /** Por que a família está fora: os motivos que o checkout daria a ela (#5797). */
  policyViolations?: readonly FarePolicyViolationSource[];
  benefits?: readonly { text: string; status: 'ok' | 'violated' | 'warn' | 'na' }[];
  baggage?: string;
  /** O que o fornecedor declarou de forma tipada, por dimensão, e QUEM
   *  declarou: o fornecedor (`wooba`) ou o mapa curado da VOLL por cima (`voll`). */
  attributes?: Readonly<Partial<Record<FareDimension, FareAttributeState>>> & {
    readonly source?: 'wooba' | 'voll';
  };
  /** Peças e quilos da mão e do despacho, quando a tarifa os declara. */
  baggageAllowance?: {
    readonly checked?: FareAllowanceLineSource | null;
    readonly carry?: FareAllowanceLineSource | null;
  } | null;
}

/** As tarifas de uma oferta: por perna, ou combos que precificam o PAR. `F` é a
 *  tarifa do host, que as funções de seleção devolvem como veio. */
export interface FlightTariffSource<F extends FlightFareSource = FlightFareSource> {
  departing: readonly F[];
  returning: readonly F[];
  bundled: readonly { fareId: string; departing: F; returning: F }[];
}
